import { GenericLazyString } from '@eventbrite/i18n';
import classNames from 'classnames';
import { Field, Form, Formik } from 'formik';
import isEmpty from 'lodash/isEmpty';
import React, { useEffect } from 'react';
import * as Yup from 'yup';
import {
    FORM_FIELD_INVALID_EMAIL,
    FORM_FIELD_REQUIRED,
} from '../../../constants/loginMessages';
import {
    IConfigContextProps,
    useConfigContext,
} from '../../../contexts/ConfigContext';
import {
    IFrontendContext,
    useFrontendContext,
} from '../../../contexts/FrontendContext';
import {
    INotificationContext,
    useNotificationsContext,
} from '../../../contexts/NotificationsContext';
import { IUserContext, useUserContext } from '../../../contexts/UserContext';
import EmailField from '../../common/EmailField';
import FormSubmitButton from '../../common/FormSubmitButton';

interface ILookupFormComponentProps {
    onSubmit: (values: any) => void;
    buttonCTA: GenericLazyString | string;
    shouldAnimate: boolean;

    trackOnPageLoad?: () => void;
}

const LookUpSchema = Yup.object().shape({
    email: Yup.string()
        .email(FORM_FIELD_INVALID_EMAIL.toString())
        .required(FORM_FIELD_REQUIRED.toString()),
});

const LookupForm = ({
    onSubmit,
    buttonCTA,
    shouldAnimate,

    trackOnPageLoad,
}: ILookupFormComponentProps) => {
    const frontendContext: IFrontendContext = useFrontendContext();
    const userContext: IUserContext = useUserContext();
    const notificationsContext: INotificationContext = useNotificationsContext();
    const configContext: IConfigContextProps = useConfigContext();
    const socialData = userContext?.state?.socialData;
    const setGeneralNotification =
        notificationsContext?.methods?.setGeneralNotification;

    useEffect(() => {
        if (trackOnPageLoad && !isEmpty(socialData)) {
            trackOnPageLoad();
        }
        return () => {
            setGeneralNotification({});
        };
    }, [setGeneralNotification, socialData, trackOnPageLoad]);

    const emailFieldClasses = 'eds-l-mar-bot-6';

    const buttonClasses = classNames({
        'eds-fx--fade-in-up eds-fx--delay-1': shouldAnimate,
    });

    const initialValues: { email: string } = { email: '' };

    return (
        <Formik
            initialValues={initialValues}
            validationSchema={LookUpSchema}
            onSubmit={(values: { email: string }) => {
                onSubmit?.({
                    values,
                    contexts: {
                        frontendContext,
                        userContext,
                        notificationsContext,
                        configContext,
                    },
                });
            }}
        >
            {({ values, setFieldValue }: any) => (
                <Form data-testid="form-lookup">
                    <Field
                        name="email"
                        component={EmailField}
                        value={values.email}
                        // submitFailed={submitFailed}
                        buttonClasses={emailFieldClasses}
                        isStatic={true}
                        setFieldValue={setFieldValue}
                        suggestDomains={true}
                    />

                    <div className="eds-l-pad-bot-10">
                        <FormSubmitButton
                            buttonClasses={buttonClasses}
                            submitDisabled={
                                frontendContext?.state.lookupOngoing
                            }
                            isLoading={frontendContext?.state.lookupOngoing}
                            buttonCTA={buttonCTA}
                        />
                    </div>
                </Form>
            )}
        </Formik>
    );
};

export default LookupForm;
